import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import { format } from 'date-fns';
import { InlineSelect, KeyboardDatePicker, MenuItem, Tooltip, Typography } from 'seagull';
import { daysOfWeek, defaultBillingDayOfWeek } from '~/constants';
import { useStoreActions } from '~/contexts';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { ordinal } from '~/infra/utils';
import { BillingDayOfMonthModel, serviceBillingModel, serviceModel } from '~/models';
import { validateBillingDayOfMonth } from '~/models/service';
import { RecurrencePeriodTypes } from '~/models/service-billing';
import { InlineUpfrontSelect } from '../BillingInitiationControl/InlineUpfrontSelect/InlineUpfrontSelect';
import styles from './BillingSettingsSection.module.scss';
import { InlineStartBillingControl } from './InlineStartBillingControl/InlineStartBillingControl';
function getDayOfMonthOptions(isBilledUpfront) {
    return new Array(28).fill(null).map((_, idx)=>({
            value: (idx + 1).toString(),
            display: ordinal(idx + 1),
            disabled: false
        })).concat({
        value: 'last',
        display: 'last day',
        disabled: isBilledUpfront
    });
}
function BillingDayOfMonthField(param) {
    let { service, billing, dayOfMonthOptions, isReadOnly, disableBillingDay, disabled, testId, showError, isActiveAgreement, Text, onHasError, onDayOfMonthChange, setShowBillingDayOfMonthError } = param;
    const { logBI } = useStoreActions();
    const isBilledUpfront = serviceModel.isBilledUpfront(service);
    const { billingDayOfMonth } = billing;
    const value = billingDayOfMonth ? billingDayOfMonth.toNormalizedDayOfMonth().toString() : 'last';
    const readOnlyValue = useMemo(()=>{
        var _dayOfMonthOptions_find;
        return ((_dayOfMonthOptions_find = dayOfMonthOptions.find((opt)=>opt.value === value)) === null || _dayOfMonthOptions_find === void 0 ? void 0 : _dayOfMonthOptions_find.display) || '';
    }, [
        dayOfMonthOptions,
        value
    ]);
    return isReadOnly ? /*#__PURE__*/ _jsx(Text, {
        children: readOnlyValue
    }) : /*#__PURE__*/ _jsx(InlineSelect, {
        skin: "primary",
        disabled: disableBillingDay || disabled,
        value: value,
        testId: "".concat(testId, "billing-day-dropdown"),
        error: showError,
        onChange: (e)=>{
            const day = e.target.value;
            logBI(11014, {
                isActiveAgreement,
                serviceId: service.id,
                day
            });
            const isLast = day === dayOfMonthOptions[dayOfMonthOptions.length - 1].value;
            const newBillingDayOfMonth = BillingDayOfMonthModel.make(isLast ? 'last' : +day);
            const hasError = validateBillingDayOfMonth(newBillingDayOfMonth, isBilledUpfront);
            setShowBillingDayOfMonthError(hasError);
            onHasError && onHasError(service.id, hasError);
            onDayOfMonthChange(service.id, newBillingDayOfMonth);
        },
        children: dayOfMonthOptions.map((param, idx)=>{
            let { value, disabled, display } = param;
            return /*#__PURE__*/ _jsx(MenuItem, {
                value: value,
                disabled: disabled,
                testId: "".concat(testId, "billing-day-option-").concat(idx),
                children: display
            }, value);
        })
    });
}
function BillingDayOfWeekField(param) {
    let { service, disableBillingDay, disabled, testId, isActiveAgreement, isReadOnly, Text, onDayOfWeekChange } = param;
    const { logBI } = useStoreActions();
    const value = serviceBillingModel.billingDayOfWeek(service.billing) || defaultBillingDayOfWeek;
    const readOnlyValue = useMemo(()=>daysOfWeek.find((day)=>day === value), [
        value
    ]);
    return isReadOnly ? /*#__PURE__*/ _jsx(Text, {
        children: readOnlyValue
    }) : /*#__PURE__*/ _jsx(InlineSelect, {
        skin: "primary",
        disabled: disableBillingDay || disabled,
        value: value,
        testId: "".concat(testId, "billing-day-dropdown"),
        onChange: (e)=>{
            logBI(11015, {
                isActiveAgreement,
                serviceId: service.id,
                day: e.target.value
            });
            onDayOfWeekChange(service.id, e.target.value);
        },
        children: daysOfWeek.map((option, idx)=>/*#__PURE__*/ _jsx(MenuItem, {
                value: option,
                testId: "".concat(testId, "billing-day-option-").concat(idx),
                children: option
            }, option))
    });
}
function BillingDayOfYearField(param) {
    let { service, isReadOnly, Text, onDayOfYearChange } = param;
    const value = useMemo(()=>serviceModel.billingDayOfYear(service) || null, [
        service
    ]);
    if (!value) {
        return null;
    }
    const readOnlyValue = useMemo(()=>format(value, 'MMMM d'), [
        value
    ]);
    return isReadOnly ? /*#__PURE__*/ _jsx(Text, {
        children: readOnlyValue
    }) : /*#__PURE__*/ _jsx(KeyboardDatePicker, {
        KeyboardButtonProps: {
            style: {
                padding: 8
            }
        },
        testId: "billing-day-year",
        format: "MMMM d",
        size: "small",
        value: new Date(value),
        onChange: (date)=>{
            if (date) {
                // TODO:ben add BI for this
                onDayOfYearChange(service.id, date.getTime());
            }
        },
        className: styles.datePicker
    });
}
function InnerBillingSettingsSection(props) {
    const { service, skin, variant, weight, className, relationshipState, isReadOnly, disabled, testId, isFreshlyAdded, onPausedChange, onBillOnAcceptanceChange, onStartBillingAtChange } = props;
    const [showBillingDayOfMonthError, setShowBillingDayOfMonthError] = useState(false);
    const isBilledUpfront = serviceModel.isBilledUpfront(service);
    const billing = serviceModel.billingTrigger(service);
    const dayOfMonthOptions = useMemo(()=>getDayOfMonthOptions(isBilledUpfront), [
        isBilledUpfront
    ]);
    const { YEARLY_AUTOMATIC_SERVICE, START_BILLING_AT_PHASE_2_INLINE } = useFeatureFlags();
    const isYearly = serviceBillingModel.recurrencePeriod(service.billing) === RecurrencePeriodTypes.Yearly;
    const shouldShowYearlyAutomaticError = isYearly && !YEARLY_AUTOMATIC_SERVICE && !serviceBillingModel.isManual(service.billing);
    const Text = (textProps)=>/*#__PURE__*/ _jsx(Typography, _object_spread({
            noWrap: true,
            skin: skin,
            variant: variant,
            weight: weight
        }, textProps));
    useEffect(()=>{
        setShowBillingDayOfMonthError(serviceModel.isMonthly(service) && validateBillingDayOfMonth(serviceModel.billingDayOfMonth(service), serviceModel.isBilledUpfront(service)));
    }, [
        service
    ]);
    const renderInlineStartBillingControl = ()=>{
        const isAgreement = (relationshipState === null || relationshipState === void 0 ? void 0 : relationshipState.type) === 'approved';
        const isReadOnlyInAgreement = isReadOnly && isAgreement;
        if (!START_BILLING_AT_PHASE_2_INLINE || !relationshipState || isReadOnlyInAgreement) {
            return null;
        }
        const isExistingServiceInEditAgreement = isAgreement && !isFreshlyAdded;
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(Typography, {
                    skin: skin,
                    variant: variant,
                    weight: weight,
                    inline: true,
                    as: "span",
                    children: ",\xa0"
                }),
                /*#__PURE__*/ _jsx(InlineStartBillingControl, {
                    service: service,
                    testId: testId,
                    disabled: isReadOnly || disabled || isExistingServiceInEditAgreement,
                    relationshipState: relationshipState,
                    onPausedChange: onPausedChange,
                    onBillOnAcceptanceChange: onBillOnAcceptanceChange,
                    onStartBillingAtChange: onStartBillingAtChange
                })
            ]
        });
    };
    const showUpfrontSelect = !START_BILLING_AT_PHASE_2_INLINE || isReadOnly;
    const renderOngoingBillingSettings = (billing)=>/*#__PURE__*/ _jsxs("div", {
            className: cn(styles.root, className),
            "data-testid": "".concat(props.testId, "-billing-settings-section"),
            children: [
                /*#__PURE__*/ _jsxs(Text, {
                    children: [
                        "Billing ",
                        START_BILLING_AT_PHASE_2_INLINE ? 'occurs' : 'is done'
                    ]
                }),
                showUpfrontSelect && /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        "\xa0",
                        /*#__PURE__*/ _jsx(InlineUpfrontSelect, {
                            service: service,
                            testId: testId,
                            disabled: props.disableBillUpfront || disabled,
                            isReadOnly: isReadOnly,
                            isActiveAgreement: props.isActiveAgreement,
                            TextComponent: Text,
                            skin: skin,
                            variant: variant,
                            weight: weight,
                            onChange: props.onBillUpfrontModeChange,
                            onHasError: props.onHasError,
                            onBillingDayOfMonthError: setShowBillingDayOfMonthError
                        })
                    ]
                }),
                (()=>{
                    switch(serviceBillingModel.period(billing)){
                        case RecurrencePeriodTypes.Monthly:
                            return /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    "\xa0",
                                    /*#__PURE__*/ _jsx(Text, {
                                        children: "on the"
                                    }),
                                    "\xa0",
                                    /*#__PURE__*/ _jsx(Tooltip, {
                                        placement: "top",
                                        title: showBillingDayOfMonthError ? 'It is not possible to bill upfront on the last day of the month. Please change the billing day.' : '',
                                        children: /*#__PURE__*/ _jsx("span", {
                                            children: /*#__PURE__*/ _jsx(BillingDayOfMonthField, _object_spread_props(_object_spread({}, props), {
                                                billing: billing,
                                                showError: showBillingDayOfMonthError,
                                                dayOfMonthOptions: dayOfMonthOptions,
                                                setShowBillingDayOfMonthError: setShowBillingDayOfMonthError,
                                                Text: Text
                                            }))
                                        })
                                    }),
                                    "\xa0",
                                    /*#__PURE__*/ _jsx(Text, {
                                        children: "of each month"
                                    }),
                                    renderInlineStartBillingControl()
                                ]
                            });
                        case RecurrencePeriodTypes.Weekly:
                        case RecurrencePeriodTypes.BiWeekly:
                            return /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    "\xa0",
                                    /*#__PURE__*/ _jsx(Text, {
                                        children: "on"
                                    }),
                                    "\xa0",
                                    /*#__PURE__*/ _jsx(BillingDayOfWeekField, _object_spread_props(_object_spread({}, props), {
                                        Text: Text
                                    })),
                                    renderInlineStartBillingControl()
                                ]
                            });
                        case RecurrencePeriodTypes.Yearly:
                            if (!YEARLY_AUTOMATIC_SERVICE) return null;
                            return /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    "\xa0",
                                    /*#__PURE__*/ _jsx(Text, {
                                        children: "each year on"
                                    }),
                                    "\xa0",
                                    /*#__PURE__*/ _jsx(BillingDayOfYearField, _object_spread_props(_object_spread({}, props), {
                                        Text: Text
                                    })),
                                    renderInlineStartBillingControl()
                                ]
                            });
                        case RecurrencePeriodTypes.Quarterly:
                            return renderInlineStartBillingControl();
                        default:
                            return null;
                    }
                })()
            ]
        });
    const ongoingBillingSettings = serviceBillingModel.isOngoing(billing) && !shouldShowYearlyAutomaticError ? renderOngoingBillingSettings(billing) : null;
    return ongoingBillingSettings;
}
export function BillingSettingsSection(props) {
    const { billing } = props.service;
    if (serviceBillingModel.isOngoing(billing)) {
        return /*#__PURE__*/ _jsx(InnerBillingSettingsSection, _object_spread({}, props));
    } else {
        return null;
    }
}
